import {
  CLASSIFIER_GRID_SIZE,
  CLASSIFIER_TILE_SIZE_PX,
  KIND_DECISION_BY_PRIOR,
  PRIOR_BY_FORMAT,
} from '../constants';
import type {CropRegion, Dimensions, ImageContentKind, PixelSample, SniffedImage} from '../types';

export type ContentMetrics = {
  readonly flatRatio: number;
  readonly uniqueRatio: number;
  readonly sampledPixels: number;
};

const CHANNELS = 4;

/** Flat-neighbour share and colour diversity over the sampled tiles. */
export const measureContent = (samples: readonly PixelSample[]): ContentMetrics => {
  let comparisons = 0;
  let flat = 0;
  let sampledPixels = 0;
  const colours = new Set<number>();

  for (const {width, height, data} of samples) {
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const offset = (y * width + x) * CHANNELS;
        const r = data[offset] ?? 0;
        const g = data[offset + 1] ?? 0;
        const b = data[offset + 2] ?? 0;
        colours.add((r << 16) | (g << 8) | b);
        sampledPixels += 1;
        if (x + 1 < width) {
          comparisons += 1;
          if (
            r === (data[offset + CHANNELS] ?? -1) &&
            g === (data[offset + CHANNELS + 1] ?? -1) &&
            b === (data[offset + CHANNELS + 2] ?? -1)
          ) {
            flat += 1;
          }
        }
      }
    }
  }

  return {
    flatRatio: comparisons === 0 ? 0 : flat / comparisons,
    uniqueRatio: sampledPixels === 0 ? 0 : colours.size / sampledPixels,
    sampledPixels,
  };
};

type Decision = (metrics: ContentMetrics) => ImageContentKind;

const DECIDE_BY_PRIOR: Readonly<Record<ImageContentKind, Decision>> = {
  photo: ({flatRatio, uniqueRatio}) =>
    flatRatio >= KIND_DECISION_BY_PRIOR.photo.graphicIfFlatRatioAtLeast &&
    uniqueRatio <= KIND_DECISION_BY_PRIOR.photo.andUniqueRatioAtMost
      ? 'graphic'
      : 'photo',
  graphic: ({flatRatio, uniqueRatio}) =>
    flatRatio <= KIND_DECISION_BY_PRIOR.graphic.photoIfFlatRatioAtMost &&
    uniqueRatio >= KIND_DECISION_BY_PRIOR.graphic.andUniqueRatioAtLeast
      ? 'photo'
      : 'graphic',
};

/** The prior stands unless the pixels say otherwise clearly; no samples → the prior. */
export const classifyImageContent = (
  samples: readonly PixelSample[],
  prior: ImageContentKind,
): ImageContentKind => {
  const metrics = measureContent(samples);
  return metrics.sampledPixels === 0 ? prior : DECIDE_BY_PRIOR[prior](metrics);
};

export const priorForSource = (sniff: SniffedImage): ImageContentKind =>
  sniff.format === 'webp'
    ? sniff.webpFlavor === 'lossless'
      ? 'graphic'
      : 'photo'
    : PRIOR_BY_FORMAT[sniff.format];

/**
 * Tiles to sample from the resized output: a grid of small squares centred on
 * fractional positions, or the whole image when it is smaller than the grid.
 */
export const classifierTiles = (image: Dimensions): readonly CropRegion[] => {
  const tile = CLASSIFIER_TILE_SIZE_PX;
  if (image.width < tile * 2 || image.height < tile * 2) {
    return [{x: 0, y: 0, width: image.width, height: image.height}];
  }
  const tiles: CropRegion[] = [];
  for (let row = 0; row < CLASSIFIER_GRID_SIZE; row += 1) {
    for (let column = 0; column < CLASSIFIER_GRID_SIZE; column += 1) {
      const centreX = ((column + 0.5) / CLASSIFIER_GRID_SIZE) * image.width;
      const centreY = ((row + 0.5) / CLASSIFIER_GRID_SIZE) * image.height;
      const x = Math.min(Math.max(Math.round(centreX - tile / 2), 0), image.width - tile);
      const y = Math.min(Math.max(Math.round(centreY - tile / 2), 0), image.height - tile);
      tiles.push({x, y, width: tile, height: tile});
    }
  }
  return tiles;
};
